import './app.css';
import { loadSession, ApiError } from './api.js';
import { el, clear } from './dom.js';
import { t } from './i18n.js';
import { renderHome } from './views/home.js';
import { renderInstitutions } from './views/institutions.js';
import { renderDates } from './views/dates.js';
import { renderPage } from './views/page.js';
import { renderAccount } from './views/account.js';
import { renderCollaborations } from './views/collaborations.js';
import { renderModeration } from './views/moderation.js';
import { renderUsers } from './views/users.js';
import { renderStats } from './views/stats.js';
import { renderCrawl } from './views/crawl.js';
import { renderHealth } from './views/health.js';

// Two areas, one bundle. A separate build for a tool a handful of people use
// earns nothing, and the boundary that matters is the API's role check, not a
// second Vite project.
const routes = [
  // The root belongs to the person; an institution is one level in, and its
  // nickname is in the address so the workspace is linkable and two of them can
  // sit in two tabs.
  { pattern: /^\/panel\/?$/, view: renderHome },
  { pattern: /^\/panel\/beteiligt\/?$/, view: renderCollaborations },
  { pattern: /^\/panel\/i\/?$/, view: renderInstitutions },
  { pattern: /^\/panel\/i\/([a-z0-9-]+)\/?$/, view: renderDates },
  { pattern: /^\/panel\/i\/([a-z0-9-]+)\/seite\/?$/, view: renderPage },
  { pattern: /^\/panel\/i\/([a-z0-9-]+)\/konto\/?$/, view: renderAccount },
  { pattern: /^\/admin\/?$/, view: renderModeration },
  { pattern: /^\/admin\/users\/?$/, view: renderUsers },
  { pattern: /^\/admin\/stats\/?$/, view: renderStats },
  { pattern: /^\/admin\/crawl\/?$/, view: renderCrawl },
  { pattern: /^\/admin\/health\/?$/, view: renderHealth },
];

// The addresses the three-page split replaced. They are behind a login, so
// nobody bookmarked them from Instagram — but a dead address in a tool you use
// daily is a bug report either way, and the venue id still names the account.
const MOVED = [
  { pattern: /^\/panel\/venue\/(\d+)(?:\/kalender)?\/?$/, to: () => '/panel' },
  { pattern: /^\/panel\/moderation\/?$/, to: () => '/admin' },
  // The flat shape, which lasted a day. It had no institution in it at all —
  // the switcher decided that — so there is nothing to carry forward and the
  // root is where the list of them lives.
  { pattern: /^\/panel\/(?:seite|konto)\/?$/, to: () => '/panel' },
];

const state = { session: null };

const root = document.getElementById('app');

boot();

async function boot() {
  const outlet = root;

  try {
    state.session = await loadSession();
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) {
      window.location.href = `/login?next=${encodeURIComponent(window.location.pathname)}`;
      return;
    }
    return fail(outlet, error);
  }

  window.addEventListener('popstate', () => route(outlet));
  // Intercept in-app links so the backend behaves like one surface. Anything
  // outside /panel (the public site, logout) is left to the browser.
  document.addEventListener('click', (event) => {
    const link = event.target.closest('a[href^="/panel"], a[href^="/admin"]');
    // /panel/api is a download, not a route: intercepting the export link would
    // swallow the click and paint "not found" over a working endpoint.
    if (!link || link.getAttribute('href').startsWith('/panel/api')) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || link.target) return;

    // Within one area only. The header is rendered by the server and injected
    // into the shell, and /panel and /admin carry *different* navigations —
    // so a client-side jump between them swapped the page under a header that
    // still belonged to where you came from, and a reload was the only way
    // back to the right one. Crossing costs one request and gets the header
    // that goes with the destination.
    if (areaOf(link.getAttribute('href')) !== areaOf(window.location.pathname)) return;

    event.preventDefault();
    navigate(link.getAttribute('href'));
  });

  route(outlet);
}

// Which of the two client-rendered areas a path belongs to. They share a
// bundle and a router; they do not share a header.
function areaOf(path) {
  return String(path).startsWith('/admin') ? 'admin' : 'panel';
}

export function navigate(path) {
  // Compare against path *and* query: the calendar navigates by month, so
  // two links can share a pathname and still be different places.
  if (path !== window.location.pathname + window.location.search) {
    window.history.pushState({}, '', path);
  }
  route(root);
}

async function route(outlet) {
  const path = window.location.pathname;

  const moved = MOVED
    .map((entry) => ({ entry, result: entry.pattern.exec(path) }))
    .find((candidate) => candidate.result);
  if (moved) {
    const to = moved.entry.to(...moved.result.slice(1));
    // Same rule as a click: a retired address that leads to the other area is
    // a real navigation, or the header stays behind with the page it came from.
    if (areaOf(to) !== areaOf(path)) {
      window.location.replace(to);
      return undefined;
    }
    window.history.replaceState({}, '', to);
    return route(outlet);
  }

  markCurrent();
  const match = routes
    .map((entry) => ({ entry, result: entry.pattern.exec(path) }))
    .find((candidate) => candidate.result);

  if (!match) {
    clear(outlet);
    outlet.append(el('p', { class: 'pub-empty' }, t.notFound));
    return;
  }

  clear(outlet);
  outlet.append(el('p', { class: 'pub-loading' }, t.loading));

  try {
    const node = await match.entry.view({
      state,
      params: match.result.slice(1),
      query: new URLSearchParams(window.location.search),
    });
    clear(outlet);
    outlet.append(node);
  } catch (error) {
    fail(outlet, error);
  }
}

function fail(outlet, error) {
  clear(outlet);
  outlet.append(
    el('div', { class: 'pub-error' }, [
      el('strong', {}, t.failed),
      el('p', { class: 'pub-error-detail' }, error.message),
    ])
  );
  console.error(error);
}

// The header ships from the server with the marker on whichever entry matched
// the first request. Client-side routing has to move it, or it points at where
// you came in rather than at where you are.
function markCurrent() {
  for (const link of document.querySelectorAll('.site-nav a[href^="/panel"], .site-nav a[href^="/admin"]')) {
    const here = window.location.pathname;
    const href = link.getAttribute('href');
    // /panel and /admin are both prefixes of their siblings, so an exact match is
    // what keeps the marker off two entries at once.
    const match = href === '/panel' || href === '/admin'
      ? here === href || here === `${href}/`
      : here.startsWith(href);
    if (match) link.setAttribute('aria-current', 'page');
    else link.removeAttribute('aria-current');
  }
}
